import { create } from 'zustand';
import { AxiosError, isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { activateTalents, deactivateTalents, getTalents, type GetTalentsParams } from '@libs/api/talents';
import {
  StaffHasActiveShiftsError,
  StaffHasActiveShiftsErrorDetails,
  StaffListItem,
} from '@libs/models/talents/talent';
import { ErrorWithDetails } from '@libs/utils';
import { DEFAULT_PAGE_SIZE } from '@molecules/dataDisplay';

type InitialState = {
  loading: boolean;
  initiated: boolean;
  page: number;
  total: number;
  perPage: number;
  talents: StaffListItem[];
  selectedTalents: string[];
  currentSearch: string;
  load: (params?: GetTalentsParams) => Promise<void>;
  setSelectedTalents: (talents: string[]) => void;
  deactivateTalents: (ids: string[]) => ReturnType<typeof deactivateTalents>;
  activateTalents: (ids: string[]) => Promise<void>;
};

export const useExternalTalentsTableListStore = create<InitialState>()((set, get) => ({
  loading: false,
  initiated: false,
  currentSearch: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  total: 0,
  talents: [],
  selectedTalents: [],
  load: async (params) => {
    set({
      loading: true,
    });
    const state = get();
    try {
      const data = await getTalents({
        page: state.page,
        size: state.perPage,
        ...params,
      });
      set({
        talents: data.items.map((t) => ({
          ...t,
          lastWorkedTime: dayjs(t.lastWorkedTime),
        })),
        total: data.totalCount,
        page: data?.pageNumber ?? 1,
        perPage: data?.pageSize ?? 10,
        initiated: true,
        currentSearch: params?.search ?? '',
        loading: false,
      });
    } finally {
      set({
        loading: false,
      });
    }
  },

  setSelectedTalents: (talents) => {
    set({ selectedTalents: talents });
  },
  deactivateTalents: async (ids: string[]) => {
    try {
      const result = await deactivateTalents(ids);

      return result;
    } catch (error) {
      if (isAxiosError(error)) {
        if (isStaffHasActiveShiftsError(error) && error.response) {
          throw new StaffHasActiveShiftsError(error.response.data);
        }
        if (typeof error.response?.data === 'object') {
          throw new ErrorWithDetails(error.response?.data.detail, { type: error.response?.data.status, values: {} });
        }
      }
      throw error;
    }
  },
  activateTalents: async (ids: string[]) => {
    await activateTalents(ids);
  },
}));

function isStaffHasActiveShiftsError(error: unknown): error is AxiosError<StaffHasActiveShiftsErrorDetails> {
  return (
    isAxiosError(error) && (error.response?.data as StaffHasActiveShiftsErrorDetails)?.type === 'staffHasActiveShifts'
  );
}
